// Ongoing Project Constants

// Validation limits for ongoing projects - in lakhs
export const ONGOING_PROJECT_VALIDATION_LIMITS = {
  PROJECT_NAME_MIN_LENGTH: 3,
  PROJECT_NAME_MAX_LENGTH: 200,
  AMOUNT_MIN: 0,
  AMOUNT_MAX: 999999, // 99,99,99 lakhs
  YEAR_MIN: 1990,
  YEAR_MAX: 2050,
  REMARKS_MAX_LENGTH: 500, // NEW
};

// NEW: Submission status constants
export const ONGOING_SUBMISSION_STATUS = {
  DRAFT: "draft",
  LOCKED: "locked",
  UNLOCKED: "unlocked",
};

// Default ongoing project structure
export const DEFAULT_ONGOING_PROJECT = {
  department: "",
  projectName: "",
  yearOfCommencement: "",
  totalEstimatedCost: 0, // in lakhs
  totalExpenditure: 0, // in lakhs
  balanceRemaining: 0, // in lakhs (auto-calculated)
  remarks: "", // NEW: Remarks field
  financialYear: "",
  createdAt: null,
  updatedAt: null,
  createdBy: null,
  updatedBy: null,
};

// NEW: Default submission structure
export const DEFAULT_ONGOING_SUBMISSION = {
  department: "",
  financialYear: "",
  isLocked: false,
  submittedAt: null,
  submittedBy: null,
  submittedByName: null,
  unlockedAt: null,
  unlockedBy: null,
  unlockedByName: null,
  createdAt: null,
  updatedAt: null,
};

// Format amount for display (in lakhs)
export const formatOngoingProjectAmount = (amount) => {
  if (!amount && amount !== 0) return "₹0.00";

  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return "₹0.00";

  return `₹${numAmount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Calculate balance remaining (Total Approved Cost - Total Clearance)
export const calculateBalanceRemaining = (
  totalEstimatedCost,
  totalExpenditure,
) => {
  const estimated = parseFloat(totalEstimatedCost) || 0;
  const expenditure = parseFloat(totalExpenditure) || 0;

  return Math.max(0, estimated - expenditure);
};

// NEW: Submission status messages
export const SUBMISSION_MESSAGES = {
  // Submit/Lock messages
  SUBMIT_CONFIRM:
    "Are you sure you want to submit and lock your ongoing projects?\n\nOnce locked, you will not be able to make any changes.",
  SUBMIT_SUCCESS: "Ongoing projects submitted and locked successfully!",

  // Unlock messages
  UNLOCK_CONFIRM:
    "Are you sure you want to unlock ongoing projects for this department?\n\nThis will allow them to make changes again.",
  UNLOCK_SUCCESS: "Ongoing projects unlocked successfully!",

  // Error messages
  ALREADY_LOCKED:
    "Ongoing projects are already locked. Please contact admin to unlock.",
  NO_PROJECTS: "No ongoing projects to submit. Please add projects first.",
  LOCKED_EDIT_WARNING:
    "Your submission is locked. You cannot edit ongoing projects.",

  // Admin messages
  ADMIN_OVERRIDE: "Editing as Administrator",
  ADMIN_UNLOCK_REQUIRED: "You can not edit this project.",

  // Status messages
  STATUS_DRAFT: "Draft - Not Yet Submitted",
  STATUS_LOCKED: "Locked & Submitted",
  STATUS_UNLOCKED: "Unlocked by Admin",

  // Info messages
  DRAFT_INFO:
    "Your ongoing projects data is saved but not locked. Remember to submit when ready.",
  LOCKED_INFO:
    "Your ongoing projects data is locked. You cannot add, edit, or delete projects.",
  LOCKED_CONTACT_ADMIN: "Your submission is locked.",

  // Confirmation messages
  CONFIRM_DELETE_PROJECT: "Are you sure you want to delete this project?",
  CONFIRM_CLEAR_FILTERS: "Clear all filters and show all projects?",

  // Success messages
  PROJECT_ADDED: "New ongoing project added successfully!",
  PROJECT_UPDATED: "Project updated successfully!",
  PROJECT_DELETED: "Project deleted successfully!",
  DATA_SAVED: "Data saved automatically.",

  // Warning messages
  UNSAVED_CHANGES:
    "You have unsaved changes. Please wait for auto-save to complete.",
  INCOMPLETE_DATA:
    "Some fields are incomplete. Please fill all required fields.",
  INVALID_AMOUNT: "Please enter a valid amount.",

  // Validation messages
  VALIDATION_REQUIRED: "This field is required.",
  VALIDATION_MIN_LENGTH: "Minimum length is {min} characters.",
  VALIDATION_MAX_LENGTH: "Maximum length is {max} characters.",
  VALIDATION_MIN_AMOUNT: "Amount must be greater than or equal to {min}.",
  VALIDATION_MAX_AMOUNT: "Amount cannot exceed {max}.",
  VALIDATION_INVALID_YEAR: "Please select a valid year.",
};

// NEW: Submission status labels for UI
export const SUBMISSION_STATUS_LABELS = {
  [ONGOING_SUBMISSION_STATUS.DRAFT]: {
    label: "Draft",
    color: "bg-blue-100 text-blue-800",
    icon: "unlock",
    description: "Not yet submitted",
  },
  [ONGOING_SUBMISSION_STATUS.LOCKED]: {
    label: "Locked",
    color: "bg-green-100 text-green-800",
    icon: "lock",
    description: "Submitted and locked",
  },
  [ONGOING_SUBMISSION_STATUS.UNLOCKED]: {
    label: "Unlocked",
    color: "bg-orange-100 text-orange-800",
    icon: "unlock",
    description: "Unlocked by admin",
  },
};

// NEW: Action types for ongoing projects
export const ONGOING_PROJECT_ACTIONS = {
  ADD: "add",
  EDIT: "edit",
  DELETE: "delete",
  SUBMIT: "submit",
  UNLOCK: "unlock",
  VIEW: "view",
  EXPORT: "export",
};

// NEW: Filter options for ongoing projects
export const ONGOING_PROJECT_FILTERS = {
  DEPARTMENT: "department",
  FINANCIAL_YEAR: "financialYear",
  YEAR_OF_COMMENCEMENT: "yearOfCommencement",
  SEARCH: "search",
  SUBMISSION_STATUS: "submissionStatus",
};

// NEW: Sort options for ongoing projects
export const ONGOING_PROJECT_SORT_OPTIONS = [
  { value: "department-asc", label: "Department (A-Z)" },
  { value: "department-desc", label: "Department (Z-A)" },
  { value: "projectName-asc", label: "Project Name (A-Z)" },
  { value: "projectName-desc", label: "Project Name (Z-A)" },
  { value: "totalEstimatedCost-desc", label: "Highest Estimated Cost" },
  { value: "totalEstimatedCost-asc", label: "Lowest Estimated Cost" },
  { value: "balanceRemaining-desc", label: "Highest Balance" },
  { value: "balanceRemaining-asc", label: "Lowest Balance" },
  { value: "yearOfCommencement-desc", label: "Most Recent Year" },
  { value: "yearOfCommencement-asc", label: "Oldest Year" },
];

// NEW: Export formats
export const EXPORT_FORMATS = {
  PDF: "pdf",
  EXCEL: "excel",
  CSV: "csv",
};

// NEW: Table column definitions for export/display
export const ONGOING_PROJECT_COLUMNS = [
  { key: "serialNo", label: "S. No.", width: 60, align: "center" },
  { key: "department", label: "Department", width: 150, align: "left" },
  { key: "projectName", label: "Project Name", width: 250, align: "left" },
  {
    key: "yearOfCommencement",
    label: "Year of Commencement",
    width: 120,
    align: "center",
  },
  {
    key: "totalEstimatedCost",
    label: "Total Approved Cost (₹ Lakh)",
    width: 150,
    align: "right",
  },
  {
    key: "totalExpenditure",
    label: "Total Clearance (₹ Lakh)",
    width: 150,
    align: "right",
  },
  {
    key: "balanceRemaining",
    label: "Balance Remaining (₹ Lakh)",
    width: 150,
    align: "right",
  },
  { key: "remarks", label: "Remarks", width: 200, align: "left" }, // NEW
];

// NEW: Permission levels
export const ONGOING_PROJECT_PERMISSIONS = {
  VIEW: "view",
  ADD: "add",
  EDIT: "edit",
  DELETE: "delete",
  SUBMIT: "submit",
  UNLOCK: "unlock",
  EXPORT: "export",
  ADMIN_OVERRIDE: "admin_override",
};

// NEW: Role-based permissions map
export const ROLE_PERMISSIONS = {
  ADMIN: [
    ONGOING_PROJECT_PERMISSIONS.VIEW,
    ONGOING_PROJECT_PERMISSIONS.ADD,
    ONGOING_PROJECT_PERMISSIONS.EDIT,
    ONGOING_PROJECT_PERMISSIONS.DELETE,
    ONGOING_PROJECT_PERMISSIONS.SUBMIT,
    ONGOING_PROJECT_PERMISSIONS.UNLOCK,
    ONGOING_PROJECT_PERMISSIONS.EXPORT,
    ONGOING_PROJECT_PERMISSIONS.ADMIN_OVERRIDE,
  ],
  DEPARTMENT: [
    ONGOING_PROJECT_PERMISSIONS.VIEW,
    ONGOING_PROJECT_PERMISSIONS.ADD,
    ONGOING_PROJECT_PERMISSIONS.EDIT,
    ONGOING_PROJECT_PERMISSIONS.DELETE,
    ONGOING_PROJECT_PERMISSIONS.SUBMIT,
    ONGOING_PROJECT_PERMISSIONS.EXPORT,
  ],
  PLANNING_OFFICIAL: [
    ONGOING_PROJECT_PERMISSIONS.VIEW,
    ONGOING_PROJECT_PERMISSIONS.EXPORT,
  ],
  DONER: [
    ONGOING_PROJECT_PERMISSIONS.VIEW,
    ONGOING_PROJECT_PERMISSIONS.EXPORT,
  ],
};

// NEW: Auto-save configuration
export const AUTO_SAVE_CONFIG = {
  DEBOUNCE_DELAY: 1000, // milliseconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 2000, // milliseconds
  SHOW_INDICATOR: true,
  INDICATOR_DURATION: 2000, // milliseconds
};

// NEW: Validation error messages
export const VALIDATION_ERRORS = {
  DEPARTMENT_REQUIRED: "Please select a department",
  PROJECT_NAME_REQUIRED: "Project name is required",
  PROJECT_NAME_TOO_SHORT: `Project name must be at least ${ONGOING_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH} characters`,
  PROJECT_NAME_TOO_LONG: `Project name cannot exceed ${ONGOING_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH} characters`,
  YEAR_REQUIRED: "Year of commencement is required",
  YEAR_INVALID: `Year must be between ${ONGOING_PROJECT_VALIDATION_LIMITS.YEAR_MIN} and ${ONGOING_PROJECT_VALIDATION_LIMITS.YEAR_MAX}`,
  AMOUNT_INVALID: "Please enter a valid amount",
  AMOUNT_NEGATIVE: "Amount cannot be negative",
  AMOUNT_TOO_LARGE: `Amount cannot exceed ₹${ONGOING_PROJECT_VALIDATION_LIMITS.AMOUNT_MAX.toLocaleString(
    "en-IN",
  )} lakhs`,
  EXPENDITURE_EXCEEDS_COST: "Total Clearance cannot exceed estimated cost",
  FINANCIAL_YEAR_REQUIRED: "Financial year is required",
  REMARKS_TOO_LONG: `Remarks cannot exceed ${ONGOING_PROJECT_VALIDATION_LIMITS.REMARKS_MAX_LENGTH} characters`, // NEW
};

// NEW: UI text constants
export const UI_TEXT = {
  // Page titles
  PAGE_TITLE: "Ongoing Projects",
  PAGE_SUBTITLE_ADMIN: "Manage ongoing projects across all departments",
  PAGE_SUBTITLE_DEPARTMENT: "Manage ongoing projects for your department",

  // Button labels
  BTN_ADD_PROJECT: "Add New Project",
  BTN_LOCK_SUBMIT: "Lock & Submit",
  BTN_UNLOCK: "Unlock for Department",
  BTN_SAVE: "Save",
  BTN_CANCEL: "Cancel",
  BTN_DELETE: "Delete",
  BTN_EXPORT: "Export",
  BTN_CLEAR_FILTERS: "Clear Filters",

  // Table headers
  TH_SERIAL: "S. No.",
  TH_DEPARTMENT: "Department",
  TH_PROJECT_NAME: "Project Name",
  TH_YEAR: "Year of Commencement",
  TH_ESTIMATED_COST: "Total Approved Cost",
  TH_EXPENDITURE: "Total Clearance",
  TH_BALANCE: "Balance Remaining",
  TH_REMARKS: "Remarks", // NEW
  TH_ACTIONS: "Actions",

  // Placeholders
  PH_SEARCH: "Search projects...",
  PH_SELECT_DEPARTMENT: "Select department",
  PH_SELECT_YEAR: "Select year",
  PH_SELECT_FY: "Select financial year",
  PH_PROJECT_NAME: "Enter project name",
  PH_AMOUNT: "0",
  PH_REMARKS: "Add remarks...", // NEW

  // Empty states
  EMPTY_NO_PROJECTS: "No ongoing projects found",
  EMPTY_NO_RESULTS: "No projects match your filters",
  EMPTY_ADD_FIRST: "Add your first ongoing project",

  // Loading states
  LOADING_PROJECTS: "Loading ongoing projects...",
  LOADING_SUBMISSION: "Loading submission status...",
  SAVING: "Saving...",
  SUBMITTING: "Submitting...",

  // Tooltips
  TT_ADD: "Add new project",
  TT_EDIT: "Edit project",
  TT_DELETE: "Delete project",
  TT_LOCKED: "Locked - Contact admin to unlock",
  TT_AUTO_CALCULATED: "Auto-calculated field",

  // Info messages
  INFO_AUTO_SAVE: "Changes are saved automatically",
  INFO_LOCKED_MODE: "Read-only mode - Data is locked",
  INFO_DRAFT_MODE: "Draft mode - Remember to submit when ready",
};

// NEW: Icon mappings for UI
export const ICON_TYPES = {
  LOCK: "lock",
  UNLOCK: "unlock",
  ADD: "add",
  EDIT: "edit",
  DELETE: "delete",
  SAVE: "save",
  EXPORT: "export",
  WARNING: "warning",
  SUCCESS: "success",
  ERROR: "error",
  INFO: "info",
};

// Export helper function to check if amount is valid
export const isValidAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") return false;
  const numAmount = parseFloat(amount);
  return (
    !isNaN(numAmount) &&
    numAmount >= ONGOING_PROJECT_VALIDATION_LIMITS.AMOUNT_MIN &&
    numAmount <= ONGOING_PROJECT_VALIDATION_LIMITS.AMOUNT_MAX
  );
};

// Export helper function to check if year is valid
export const isValidYear = (year) => {
  if (!year) return false;
  const numYear = parseInt(year);
  return (
    !isNaN(numYear) &&
    numYear >= ONGOING_PROJECT_VALIDATION_LIMITS.YEAR_MIN &&
    numYear <= ONGOING_PROJECT_VALIDATION_LIMITS.YEAR_MAX
  );
};

// Export helper function to check if project name is valid
export const isValidProjectName = (name) => {
  if (!name || typeof name !== "string") return false;
  const trimmedName = name.trim();
  return (
    trimmedName.length >=
      ONGOING_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH &&
    trimmedName.length <=
      ONGOING_PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH
  );
};

// Export helper to format validation error messages with parameters
export const formatValidationError = (errorKey, params = {}) => {
  let message = VALIDATION_ERRORS[errorKey] || errorKey;

  // Replace placeholders in message
  Object.keys(params).forEach((key) => {
    message = message.replace(`{${key}}`, params[key]);
  });

  return message;
};

// Export default to support both named and default imports
export default {
  ONGOING_PROJECT_VALIDATION_LIMITS,
  ONGOING_SUBMISSION_STATUS,
  DEFAULT_ONGOING_PROJECT,
  DEFAULT_ONGOING_SUBMISSION,
  formatOngoingProjectAmount,
  calculateBalanceRemaining,
  SUBMISSION_MESSAGES,
  SUBMISSION_STATUS_LABELS,
  ONGOING_PROJECT_ACTIONS,
  ONGOING_PROJECT_FILTERS,
  ONGOING_PROJECT_SORT_OPTIONS,
  EXPORT_FORMATS,
  ONGOING_PROJECT_COLUMNS,
  ONGOING_PROJECT_PERMISSIONS,
  ROLE_PERMISSIONS,
  AUTO_SAVE_CONFIG,
  VALIDATION_ERRORS,
  UI_TEXT,
  ICON_TYPES,
  isValidAmount,
  isValidYear,
  isValidProjectName,
  formatValidationError,
};
